// ============================================
// TotalValueCardBlue - Solid Blue KPI Card
// Purchase Orders page right-side value card
// ============================================

import { cn } from '../../../utils/cn';
import { useMoney } from '../../../providers/CurrencyProvider';

// ============================================
// Types
// ============================================

import { useI18n } from '../../../i18n';

interface TotalValueCardBlueProps {
  label?: string;
  value: number;
  delta?: number; // percentage change (hidden when not provided)
  deltaLabel?: string;
  /**
   * Số phụ hiển thị dưới số chính. Dùng cho cặp "đặt hàng / thực nhận" ở tab
   * Stock Imports: `total_cost` và `received_total_cost` là hai cách đo khác
   * nhau trên CÙNG một tập phiếu, không thay thế được cho nhau. Bỏ trống thì
   * thẻ giữ nguyên hình dạng cũ.
   */
  secondaryValue?: number;
  secondaryLabel?: string;
  className?: string;
}

// ============================================
// Component
// ============================================

export function TotalValueCardBlue({
  label,
  value,
  delta,
  deltaLabel,
  secondaryValue,
  secondaryLabel,
  className,
}: TotalValueCardBlueProps) {
  const { t } = useI18n();
  const money = useMoney();
  const displayLabel = label || t('totalValueMtd') || 'TOTAL VALUE (MTD)';
  const displayDeltaLabel = deltaLabel || t('vsLastMonth') || 'vs last month';
  const displaySecondaryLabel = secondaryLabel || t('receivedValueMtd') || 'RECEIVED VALUE (MTD)';
  const isPositive = (delta ?? 0) >= 0;
  const hasSecondary = typeof secondaryValue === 'number' && Number.isFinite(secondaryValue);

  // Format currency value
  const formattedValue = money.format(value);

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-xl bg-[#004AC6] p-6',
        className
      )}
    >
      {/* Decorative texture - rotated and scaled */}
      <div
        className="pointer-events-none absolute -right-10 -bottom-10 h-40 w-40 opacity-20"
        style={{
          background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)',
          transform: 'rotate(55.87deg) scale(1.5)',
        }}
      />
      
      {/* Content */}
      <div className="relative z-10">
        {/* Label */}
        <p
          className="text-[10px] font-bold uppercase tracking-[1px]"
          style={{ color: 'rgba(219, 225, 255, 0.6)' }}
        >
          {displayLabel}
        </p>

        {/* Value */}
        <p className="mt-2 font-sans text-[30px] font-extrabold leading-tight text-white">
          {formattedValue}
        </p>

        {/* Số thực nhận — chỉ hiện khi phía gọi truyền vào */}
        {hasSecondary && (
          <div className="mt-3 border-t border-white/20 pt-2">
            <p
              className="text-[10px] font-bold uppercase tracking-[1px]"
              style={{ color: 'rgba(219, 225, 255, 0.6)' }}
            >
              {displaySecondaryLabel}
            </p>
            <p className="mt-0.5 font-sans text-lg font-bold leading-tight text-white">
              {money.format(secondaryValue as number)}
            </p>
          </div>
        )}

        {/* Delta — only when a real value is supplied */}
        {delta !== undefined && delta !== null && (
          <div className="mt-2 flex items-center gap-1">
            {/* Arrow icon */}
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className={cn(!isPositive && 'rotate-180')}
            >
              <path
                d="M6 2.5L10 6.5H7V9.5H5V6.5H2L6 2.5Z"
                fill="#DBE1FF"
              />
            </svg>
            <span className="text-xs font-medium" style={{ color: '#DBE1FF' }}>
              {isPositive ? '+' : ''}{delta.toFixed(1)}% {displayDeltaLabel}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
